import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Clock, Play, Square, Timer, Calendar } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow, format } from "date-fns";
import type { AssistantTimeEntry, Task, Goal } from "@shared/schema";
import { Link } from "wouter";
import { isFutureGoal } from "@/lib/goal-visibility";

interface TimeClockWidgetProps {
  maxRecentEntries?: number;
}

interface TimeEntryWithDetails extends AssistantTimeEntry {
  task?: { id: string; title: string } | null;
  goal?: { id: string; title: string } | null;
}

export function TimeClockWidget({ maxRecentEntries = 5 }: TimeClockWidgetProps) {
  const { toast } = useToast();
  const [elapsedTime, setElapsedTime] = useState<string>("00:00:00");
  const [description, setDescription] = useState("");
  const [selectedTaskId, setSelectedTaskId] = useState<string>("");
  const [selectedGoalId, setSelectedGoalId] = useState<string>("");

  const { data: activeEntry, isLoading: activeLoading } = useQuery<TimeEntryWithDetails | null>({
    queryKey: ["/api/time-entries/active"],
  });

  const { data: recentEntries = [], isLoading: entriesLoading } = useQuery<TimeEntryWithDetails[]>({
    queryKey: ["/api/time-entries"],
  });

  const { data: tasks = [] } = useQuery<Task[]>({
    queryKey: ["/api/tasks"],
  });

  const { data: allGoals = [] } = useQuery<Goal[]>({
    queryKey: ["/api/goals"],
  });
  const goals = allGoals.filter(goal => !isFutureGoal(goal));

  const clockInMutation = useMutation({
    mutationFn: (data: { taskId?: string; goalId?: string; description?: string }) =>
      apiRequest("POST", "/api/time-entries/clock-in", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/time-entries/active"] });
      queryClient.invalidateQueries({ queryKey: ["/api/time-entries"] });
      setDescription("");
      setSelectedTaskId("");
      setSelectedGoalId("");
      toast({
        title: "Clocked In",
        description: "You have successfully clocked in.",
      });
    },
    onError: (error: any) => {
      // The server may reject because another tab/device already clocked in.
      // Refetch the active entry so the widget flips to the running-timer state.
      queryClient.invalidateQueries({ queryKey: ["/api/time-entries/active"] });
      queryClient.invalidateQueries({ queryKey: ["/api/time-entries"] });
      toast({
        title: "Clock In Failed",
        description: error?.message || "Failed to clock in. Please try again.",
        variant: "destructive",
      });
    },
  });

  const clockOutMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest("POST", `/api/time-entries/${id}/clock-out`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/time-entries/active"] });
      queryClient.invalidateQueries({ queryKey: ["/api/time-entries"] });
      toast({
        title: "Clocked Out",
        description: "You have successfully clocked out.",
      });
    },
    onError: (error: any) => {
      // The entry may already be closed by another tab/device. Refetch the
      // active entry so the widget flips back to the Clock In form.
      queryClient.invalidateQueries({ queryKey: ["/api/time-entries/active"] });
      queryClient.invalidateQueries({ queryKey: ["/api/time-entries"] });
      toast({
        title: "Clock Out Failed",
        description: error?.message || "Failed to clock out. Please try again.",
        variant: "destructive",
      });
    },
  });

  useEffect(() => {
    if (!activeEntry?.clockInAt) {
      setElapsedTime("00:00:00");
      return;
    }

    const updateElapsed = () => {
      const start = new Date(activeEntry.clockInAt).getTime();
      const now = Date.now();
      const diff = now - start;

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setElapsedTime(
        `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
      );
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);

    return () => clearInterval(interval);
  }, [activeEntry?.clockInAt]);

  const handleClockIn = () => {
    clockInMutation.mutate({
      taskId: selectedTaskId || undefined,
      goalId: selectedGoalId || undefined,
      description: description || undefined,
    });
  };

  const handleClockOut = () => {
    if (activeEntry) {
      clockOutMutation.mutate(activeEntry.id);
    }
  };

  const formatDuration = (minutes: number | null) => {
    if (!minutes) return "—";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  if (activeLoading) {
    return (
      <div className="space-y-4" data-testid="time-clock-widget-loading">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="time-clock-widget">
      {activeEntry ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Badge variant="default" className="bg-green-600 hover:bg-green-700" data-testid="status-clocked-in">
              <Clock className="w-3 h-3 mr-1" />
              Clocked In
            </Badge>
            <span className="text-xs text-muted-foreground">
              Started {formatDistanceToNow(new Date(activeEntry.clockInAt), { addSuffix: true })}
            </span>
          </div>

          <div className="text-center py-6 bg-muted/50 rounded-lg">
            <div className="text-4xl font-mono font-bold text-primary" data-testid="elapsed-time">
              {elapsedTime}
            </div>
            <p className="text-sm text-muted-foreground mt-2">Elapsed Time</p>
          </div>

          {(activeEntry.task || activeEntry.goal || activeEntry.description) && (
            <div className="space-y-2 p-3 bg-muted/30 rounded-lg">
              {activeEntry.task && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Task:</span>
                  <span className="font-medium" data-testid="current-task">{activeEntry.task.title}</span>
                </div>
              )}
              {activeEntry.goal && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Goal:</span>
                  <span className="font-medium" data-testid="current-goal">{activeEntry.goal.title}</span>
                </div>
              )}
              {activeEntry.description && (
                <div className="text-sm">
                  <span className="text-muted-foreground">Description:</span>
                  <p className="mt-1" data-testid="current-description">{activeEntry.description}</p>
                </div>
              )}
            </div>
          )}

          <Button
            onClick={handleClockOut}
            disabled={clockOutMutation.isPending}
            className="w-full"
            variant="destructive"
            data-testid="button-clock-out"
          >
            <Square className="w-4 h-4 mr-2" />
            {clockOutMutation.isPending ? "Clocking Out..." : "Clock Out"}
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Badge variant="secondary" data-testid="status-clocked-out">
              <Timer className="w-3 h-3 mr-1" />
              Not Clocked In
            </Badge>
          </div>

          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="clock-in-task">Task (Optional)</Label>
              <Select value={selectedTaskId} onValueChange={setSelectedTaskId}>
                <SelectTrigger data-testid="select-task">
                  <SelectValue placeholder="Select a task..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No task</SelectItem>
                  {tasks.filter(t => !t.completed).map((task) => (
                    <SelectItem key={task.id} value={task.id} data-testid={`task-option-${task.id}`}>
                      {task.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="clock-in-goal">Goal (Optional)</Label>
              <Select value={selectedGoalId} onValueChange={setSelectedGoalId}>
                <SelectTrigger data-testid="select-goal">
                  <SelectValue placeholder="Select a goal..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No goal</SelectItem>
                  {goals.filter(g => g.status === 'active').map((goal) => (
                    <SelectItem key={goal.id} value={goal.id} data-testid={`goal-option-${goal.id}`}>
                      {goal.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="clock-in-description">Description (Optional)</Label>
              <Textarea
                id="clock-in-description"
                placeholder="What will you be working on?"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                data-testid="input-description"
              />
            </div>
          </div>

          <Button
            onClick={handleClockIn}
            disabled={clockInMutation.isPending}
            className="w-full"
            data-testid="button-clock-in"
          >
            <Play className="w-4 h-4 mr-2" />
            {clockInMutation.isPending ? "Clocking In..." : "Clock In"}
          </Button>
        </div>
      )}

      <div className="border-t pt-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium">Recent Entries</h4>
          <Link href="/time-entries">
            <Button variant="ghost" size="sm" data-testid="button-view-all-entries">
              View All
            </Button>
          </Link>
        </div>

        {entriesLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : recentEntries.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4" data-testid="no-recent-entries">
            No time entries yet
          </p>
        ) : (
          <div className="space-y-2">
            {recentEntries.slice(0, maxRecentEntries).map((entry) => (
              <div
                key={entry.id}
                className="flex items-center justify-between p-2 bg-muted/30 rounded-lg text-sm"
                data-testid={`recent-entry-${entry.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-3 h-3 text-muted-foreground" />
                    <span className="text-muted-foreground">
                      {format(new Date(entry.clockInAt), "MMM d, yyyy")}
                    </span>
                    {!entry.clockOutAt && (
                      <Badge variant="outline" className="text-xs">Active</Badge>
                    )}
                  </div>
                  {(entry.task || entry.goal) && (
                    <p className="text-xs text-muted-foreground mt-1 truncate">
                      {entry.task?.title || entry.goal?.title}
                    </p>
                  )}
                </div>
                <div className="font-medium" data-testid={`entry-duration-${entry.id}`}>
                  {entry.clockOutAt ? formatDuration(entry.durationMinutes) : "In Progress"}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
